import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Clock, CheckCircle, XCircle, ArrowRight, Search, Building2, DollarSign, UserPlus, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { associationApi, orderApi, documentApi, enhancedDocumentApi } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";
import { OrderStatus, Association, Order } from "@/models";
import { AssociationSearchParams, PaginatedResponse as AssociationPaginatedResponse } from "@/services/api/associationApi";
import { OrderSearchParams } from "@/models/Order";
import { DocumentSearchParams, DocumentsResponse } from "@/models/Document";
import { extractDataArray } from "@/utils/apiUtils";

// Define the orders paginated response type (from orderApi)
interface OrdersPaginatedResponse {
  data: Order[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const Dashboard = () => {
  const { user, hasPermission, getCurrentRoleIds, selectedRole } = useAuth();
  const { toast } = useToast();
  
  // Helper function to get the current management company ID based on selected role
  const getCurrentManagementCompanyId = () => {
    if (!selectedRole) return undefined;
    
    // For platform roles, don't filter by management company
    if (selectedRole.type === 'platform') {
      return undefined;
    }
    
    // For management company roles, use the selected entity ID
    if (selectedRole.type === 'management_company') {
      return selectedRole.entityId;
    }
    
    // For association roles, we need to get the management company ID from the user
    // as associations are under management companies
    if (selectedRole.type === 'association') {
      return user?.management_company_id;
    }
    
    return undefined;
  };

  const currentManagementCompanyId = getCurrentManagementCompanyId();
  
  const associationSearchParams: AssociationSearchParams = {
    ...(currentManagementCompanyId && { management_company_id: currentManagementCompanyId })
  };
  
  const orderSearchParams: OrderSearchParams = {
    include_customer: true,
    ...(currentManagementCompanyId && { management_company_id: currentManagementCompanyId })
  };
  
  const documentSearchParams: DocumentSearchParams = {
    ...(currentManagementCompanyId && { management_company_id: currentManagementCompanyId })
  };
  
  const { 
    data: associationsResponse = {} as AssociationPaginatedResponse<Association> | Association[], 
    isLoading: associationsLoading 
  } = useQuery<AssociationPaginatedResponse<Association> | Association[], Error, AssociationPaginatedResponse<Association> | Association[], [string, AssociationSearchParams]>({
    queryKey: ['associations', associationSearchParams],
    queryFn: () => associationApi.list({ queryKey: ['associations', associationSearchParams] }),
    enabled: selectedRole !== null,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading associations",
          description: error.message || "Failed to fetch associations",
        });
      }
    }
  });

  const associations = extractDataArray<Association>(associationsResponse);
  const totalAssociationsCount = 
    'metadata' in associationsResponse && 
    associationsResponse.metadata?.total_count !== undefined
      ? associationsResponse.metadata.total_count 
      : associations.length;

  const { 
    data: ordersResponse = { data: [], total: 0, page: 1, pageSize: 10, totalPages: 0 }, 
    isLoading: ordersLoading 
  } = useQuery<OrdersPaginatedResponse, Error, OrdersPaginatedResponse, [string, OrderSearchParams]>({
    queryKey: ['orders', orderSearchParams],
    queryFn: () => orderApi.list(orderSearchParams),
    enabled: selectedRole !== null,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading orders",
          description: error.message || "Failed to fetch orders",
        });
      }
    }
  });

  // Extract orders from the paginated response
  const orders = ordersResponse?.data || [];

  const { 
    data: recentDocuments = {data: [], meta: {total_count: 0}}, 
    isLoading: isLoadingDocuments
  } = useQuery<DocumentsResponse, Error, DocumentsResponse, [string, DocumentSearchParams]>({
    queryKey: ['documents', documentSearchParams],
    queryFn: () => enhancedDocumentApi.list(documentSearchParams),
    enabled: selectedRole !== null,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading documents",
          description: error.message,
        });
      }
    }
  });

  const isLoading = associationsLoading || ordersLoading || isLoadingDocuments;

  const recentOrders = Array.isArray(orders) 
    ? [...orders]
        .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
        .slice(0, 3)
    : [];

  console.log("Dashboard filtering debug:", {
    selectedRole,
    currentManagementCompanyId,
    associationsCount: associations.length,
    ordersCount: orders.length,
    documentsCount: Array.isArray(recentDocuments) ? recentDocuments.length : 0
  });

  const getCustomerName = (order: any) => {
    if (order.customer?.first_name && order.customer?.last_name) {
      return `${order.customer.first_name} ${order.customer.last_name}`;
    }
    return `Customer ID: ${order.customer_id}`;
  };

  const getCustomerEmail = (order: any) => {
    return order.customer?.email;
  };

  const getAssociationName = (order: any) => {
    return order.association?.name || `Association ID: ${order.association_id}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "under_review":
        return <Clock className="h-5 w-5 text-blue-500" />;
      case "denied":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "application_submitted":
        return <FileText className="h-5 w-5 text-yellow-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return '';
    
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    }).format(new Date(date));
  };

  // Calculate statistics directly for use in the UI
  const totalDocumentsCount = Array.isArray(recentDocuments.data) ? recentDocuments.data.length : 0;
  
  const pendingOrdersCount = Array.isArray(orders) 
    ? orders.filter(order => 
        order.status === OrderStatus.under_review || 
        order.status === OrderStatus.application_submitted).length
    : 0;
    
  // Updated revenue calculation to exclude draft and cancelled orders
  const totalRevenue = Array.isArray(orders)
    ? orders.filter(order => 
        order.status !== OrderStatus.draft && 
        order.status !== OrderStatus.cancelled
      )
      .reduce((sum, order) => sum + (order.total_amount || 0), 0)
    : 0;

  // Permission check functions
  const canCreateAssociation = () => {
    const currentRoleIds = getCurrentRoleIds();
    return hasPermission(currentRoleIds, 'write', 'associations');
  };
  
  const canCreateDocument = () => {
    const currentRoleIds = getCurrentRoleIds();
    return hasPermission(currentRoleIds, 'write', 'documents');
  };
  
  const canCreateUser = () => {
    const currentRoleIds = getCurrentRoleIds();
    return hasPermission(currentRoleIds, 'write', 'users');
  };

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-gray-500">
            Welcome back, {user?.firstName || 'User'}!
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
          </div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Total Associations
                  </CardTitle>
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{totalAssociationsCount}</div>
                  
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Documents
                  </CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{totalDocumentsCount}</div>
                  
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pending Orders</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{pendingOrdersCount}</div>
                  
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Total Revenue
                  </CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">${totalRevenue.toFixed(2)}</div>
                 
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recent Orders</CardTitle>
                <CardDescription>
                  Recent document requests from buyers
                </CardDescription>
              </CardHeader>
              <CardContent>
                {recentOrders.length > 0 ? (
                  <div className="space-y-4">
                    {recentOrders.map((order) => (
                      <div
                        key={order.id}
                        className="flex items-center justify-between border rounded-lg p-4"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="hidden sm:block">
                            {getStatusIcon(order.status || '')}
                          </div>
                          <div>
                            <div className="flex flex-col space-y-1">
                              <div className="font-medium">
                                {getCustomerName(order)}
                              </div>
                              {getCustomerEmail(order) && (
                                <div className="text-sm text-muted-foreground">
                                  {getCustomerEmail(order)}
                                </div>
                              )}
                              <div className="text-sm text-muted-foreground">
                                {getAssociationName(order)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Order #{order.id} • {formatDate(order.created_at)}
                              </div>
                            </div>
                          </div>
                        </div>
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/backoffice/orders/${order.id}`}>
                            Review
                          </Link>
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                    <h3 className="text-lg font-medium">No recent orders</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      There are no recent orders in the system
                    </p>
                  </div>
                )}
              </CardContent>
              <CardFooter>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/backoffice/orders">View All Orders</Link>
                </Button>
              </CardFooter>
            </Card>

            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {canCreateAssociation() && (
                    <Button asChild className="w-full justify-start" variant="outline">
                      <Link to="/backoffice/associations/new">
                        <Building2 className="mr-2 h-4 w-4" />
                        Add Association
                      </Link>
                    </Button>
                  )}
                  {canCreateDocument() && (
                    <Button asChild className="w-full justify-start" variant="outline">
                      <Link to="/backoffice/documents/new">
                        <FileText className="mr-2 h-4 w-4" />
                        Create Document
                      </Link>
                    </Button>
                  )}
                  {canCreateUser() && (
                    <Button asChild className="w-full justify-start" variant="outline">
                      <Link to="/backoffice/users/new">
                        <UserPlus className="mr-2 h-4 w-4" />
                        Add User
                      </Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </BackofficeLayout>
  );
};

export default Dashboard;